import { CustomerOverviewActionTypes, ActionFetchKits, ActionFetchKitsSuccess, ActionFetchProfilesSuccess, ActionClearSearchParameters, ActionFetchKitsByStatus, ActionSetTablePage, ActionSetTableSearchTerm, CustomerThunkDispatch, ActionFetchKitOverViewSuccess } from './types';
import { KitService } from './effects';
import { ProfileService } from '../profile/effects';
import { LatestKitsPayload, Profile, KitStatus, Kit, getKitProfileId, EMPTY_KIT } from '../../models/customerOverview';
import { isNotUndefined } from '../../helpers/helpers';
import { KitOverviewResponse } from '../../containers/Customers/component/Dial';

export const dispatchClearSearchParameters = (): ActionClearSearchParameters => {
  return {
    type: CustomerOverviewActionTypes.CLEAR_SEARCH_PARAMETERS,
  };
};

export const dispatchFetchKits = (): ActionFetchKits => {
  return {
    type: CustomerOverviewActionTypes.START_FETCHING_KITS,
  };
};

export const dispatchFetchKitsSuccess = (payload: LatestKitsPayload): ActionFetchKitsSuccess => {
  return {
    type: CustomerOverviewActionTypes.FETCH_KITS_SUCCESS,
    payload,
  };
};

export const dispatchFetchKitStatusBartSuccess = (payload: KitOverviewResponse): ActionFetchKitOverViewSuccess => {
  return {
    type: CustomerOverviewActionTypes.SET_KIT_STATUS_OVERVIEW_SUCCESS,
    payload,
  };
};

export const dispatchFetchProfilesSuccess = (payload: Profile[]): ActionFetchProfilesSuccess => {
  return {
    type: CustomerOverviewActionTypes.FETCH_PROFILES_SUCCESS,
    payload,
  };
};

export const dispatchFetchKitsByStatus = (payload: KitStatus): ActionFetchKitsByStatus => {
  return {
    type: CustomerOverviewActionTypes.FETCH_KITS_BY_STATUS,
    payload,
  };
};

export const dispatchSetTablePage = (payload: number): ActionSetTablePage => {
  return {
    type: CustomerOverviewActionTypes.SET_TABLE_PAGE,
    payload,
  };
};

export const dispatchSetTableSearchTerm = (payload: string): ActionSetTableSearchTerm => {
  return {
    type: CustomerOverviewActionTypes.SET_TABLE_SEARCH_TERM,
    payload,
  };
};

export const actionFetchLatestKits = () => async (dispatch: CustomerThunkDispatch) => {
  dispatch(dispatchClearSearchParameters());
  dispatch(dispatchSetTablePage(1));
  dispatch(dispatchFetchKits());
  const kits = await KitService.fetchLatestKits();
  dispatch(dispatchFetchKitsSuccess(kits));
  fetchProfiles(dispatch, kits.kits);
};

export const actionFetchKits = (from: number, size: number, status: KitStatus | undefined) => async (dispatch: CustomerThunkDispatch) => {
  dispatch(dispatchFetchKits());
  const kits = await KitService.fetchKits(from, size, status);
  dispatch(dispatchFetchKitsSuccess(kits));
  fetchProfiles(dispatch, kits.kits);
};

export const actionFetchKitsByStatus = (status: KitStatus, quantity?: string | number) => async (dispatch: CustomerThunkDispatch) => {
  dispatch(dispatchFetchKits());
  dispatch(dispatchClearSearchParameters());
  dispatch(dispatchSetTablePage(1));
  dispatch(dispatchSetTableSearchTerm(status));
  dispatch(dispatchFetchKitsByStatus(status));
  const kits = (quantity && quantity !== '0') ? await KitService.fetchKits(undefined, undefined, status) : { kits: [], total: 0 };
  dispatch(dispatchFetchKitsSuccess(kits));
  fetchProfiles(dispatch, kits.kits);
};

export const actionFetchKitsOverView = () => async (dispatch: CustomerThunkDispatch) => {
  const kitsOverview = await KitService.fetchKitsOverViewresponse();
  dispatch(dispatchFetchKitStatusBartSuccess(kitsOverview));
};

export const actionFetchKitsByBarcode = (barcode: string) => (dispatch: CustomerThunkDispatch) => {
  return fetchSingleKit(dispatch, KitService.fetchKitByBarcode, barcode);
};

export const actionFetchKitsByKitId = (kitId: string) => (dispatch: CustomerThunkDispatch) => {
  return fetchSingleKit(dispatch, KitService.fetchKitByKitId, kitId);
};

async function fetchSingleKit(
  dispatch: CustomerThunkDispatch,
  fetcher: (barcodeOrKitId: string) => Promise<Kit | undefined>,
  barcodeOrKitId: string,
) {
  dispatch(dispatchFetchKits());
  dispatch(dispatchClearSearchParameters());
  dispatch(dispatchSetTablePage(1));
  dispatch(dispatchSetTableSearchTerm(barcodeOrKitId));
  const kit = await fetcher(barcodeOrKitId);
  const kits = kit
    ? { kits: [kit], total: '1' }
    : { kits: [], total: '0' };
  dispatch(dispatchFetchKitsSuccess(kits));
  fetchProfiles(dispatch, kits.kits);
}

export const actionFetchKitsByEmail = (email: string) => async (dispatch: CustomerThunkDispatch) => {
  dispatch(dispatchFetchKits());
  dispatch(dispatchClearSearchParameters());
  dispatch(dispatchSetTablePage(1));
  dispatch(dispatchSetTableSearchTerm(email));
  const profiles = (await ProfileService.fetchProfilesByEmail(email));
  const kits = await KitService.fetchKitsByProfileIds(profiles.map(p => p.profileId));
  dispatch(dispatchFetchProfilesSuccess(profiles));
  const merged = mergeProfilesWithKits(profiles, kits);
  dispatch(dispatchFetchKitsSuccess({
    kits: merged,
    total: merged.length,
  }));
};

export const actionFetchKitsByCustomerName = (customerName: string) => async (dispatch: CustomerThunkDispatch) => {
  dispatch(dispatchFetchKits());
  dispatch(dispatchClearSearchParameters());
  dispatch(dispatchSetTablePage(1));
  dispatch(dispatchSetTableSearchTerm(customerName));
  const profiles = (await ProfileService.fetchProfilesByCustomerName(customerName));
  const kits = await KitService.fetchKitsByProfileIds(profiles.map(p => p.profileId));
  dispatch(dispatchFetchProfilesSuccess(profiles));
  const merged = mergeProfilesWithKits(profiles, kits);
  dispatch(dispatchFetchKitsSuccess({
    kits: merged,
    total: merged.length,
  }));
};

export const actionSetTablePage = (page: number) => (dispatch: CustomerThunkDispatch) => {
  dispatch(dispatchSetTablePage(page));
};

export const actionSetTableSearchTerm = (searchTerm: string) => (dispatch: CustomerThunkDispatch) => {
  dispatch(dispatchSetTableSearchTerm(searchTerm));
};

async function fetchProfiles(dispatch: CustomerThunkDispatch, kits: Kit[]) {
  const profileIds = kits
    .map(getKitProfileId)
    .filter(isNotUndefined);
  const profiles = await ProfileService.fetchProfiles(profileIds);
  dispatch(dispatchFetchProfilesSuccess(profiles));
}

function mergeProfilesWithKits(profiles: Profile[], kits: Kit[]): Kit[] {
  return profiles.reduce<Kit[]>((allKits, profile) => {
    const thisProfileKits = kits.filter(kit => getKitProfileId(kit) === profile.profileId);
    if (thisProfileKits.length > 0) {
      allKits.push(...thisProfileKits);
    } else {
      // For profiles without an activated kit, create a fake kit with
      // kit status = "No Kit", so we can display them in the search results too.
      allKits.push({
        ...EMPTY_KIT,
        profile: {
          kitProfileId: '',
          profileId: profile.profileId,
          owner: profile.owner,
        },
      });
    }
    return allKits;
  }, []);
}